import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import { FormGroup } from '@angular/forms';

export interface GoogleMapsAddressInterface {
  premise?: string;
  subpremise?: string;
  street_number?: string;
  route?: string;
  neighborhood?: string;
  sublocality_level_3?: string;
  sublocality_level_2?: string;
  sublocality_level_1?: string;
  sublocality?: string;
  locality?: string;
  administrative_area_level_1?: string;
  postal_code?: string;
  country?: string;
  formatted_address?: string;
}

export interface AddressFormMapping {
  street?: string;
  country?: string;
  state?: string;
  city?: string;
  zip?: string;
  unitNumber?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsAutocompleteService {
  private addressSelected = new Subject<GoogleMapsAddressInterface>();
  addressSelected$ = this.addressSelected.asObservable();

  constructor(private zone: NgZone) {}

  initializeAutocomplete(
    inputElement: HTMLElement,
    form: FormGroup,
    formMapping: AddressFormMapping,
    countryList?: any[],
    countryName?: string,
    onAddressSelect?: (address: GoogleMapsAddressInterface) => void
  ): any {
    try {
      if (!(window as any).google || !(window as any).google.maps || !(window as any).google.maps.places) {
        console.warn('Google Maps Places API not loaded');
        return null;
      }
      const options = {
        types: ['address'],
        // componentRestrictions: { country: 'US' },
      };

      if (countryName) {
        const extractCountryCode = countryList.length ? this.getCountryCode(countryName, countryList) : 'US';
        options['componentRestrictions'] = { country: extractCountryCode };
      }

      const autocomplete = new (window as any).google.maps.places.Autocomplete(inputElement, options);

      autocomplete.addListener('place_changed', () => {
        this.zone.run(() => {
          const place = autocomplete.getPlace();
          if (place.address_components && place.geometry) {
            const shortNameComponents = this.getShortNameComponents(place);
            this.addressSelected.next(shortNameComponents);
            const addressComponents = this.extractAddressComponents(place);

            if (form && formMapping) {
              this.updateFormWithAddress(form, addressComponents, formMapping);
            }
            if (onAddressSelect) {
              onAddressSelect(shortNameComponents);
            }
          }
        });
      });

      return autocomplete;
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
      return null;
    }
  }

  private extractAddressComponents(place: any): GoogleMapsAddressInterface {
    const components: GoogleMapsAddressInterface = {
      subpremise: '',
      premise: '',
      street_number: '',
      route: '',
      neighborhood: '',
      sublocality_level_3: '',
      sublocality_level_2: '',
      sublocality_level_1: '',
      sublocality: '',
      locality: '',
      administrative_area_level_1: '',
      postal_code: '',
      country: '',
      formatted_address: place.formatted_address,
    };

    place.address_components.forEach((component) => {
      const type = component.types[0];
      if (components.hasOwnProperty(type)) {
        components[type] = component.long_name;
      }
    });

    return components;
  }

  private getShortNameComponents(place: any) {
    const addressComponents = {
      country: '',
      administrative_area_level_1: '',
      formatted_address: place.formatted_address,
    };

    place.address_components?.forEach((component) => {
      if (component.types.includes('country')) {
        addressComponents.country = component.short_name;
      }
      if (component.types.includes('administrative_area_level_1')) {
        addressComponents.administrative_area_level_1 = component.short_name;
      }
    });

    return addressComponents;
  }

  private updateFormWithAddress(form: FormGroup, address: GoogleMapsAddressInterface, mapping: AddressFormMapping) {
    const addressMapping = {
      street: (formattedAddress: GoogleMapsAddressInterface) => {
        const {
          street_number = '',
          route = '',
          sublocality = '',
          sublocality_level_1 = '',
          sublocality_level_2 = '',
          sublocality_level_3 = '',
          neighborhood = '',
          premise = '',
        } = formattedAddress;
        const parts = [
          premise,
          street_number,
          route,
          neighborhood,
          sublocality_level_3,
          sublocality_level_2,
          sublocality_level_1 || sublocality,
        ].filter((part) => part);

        return parts.length > 0 ? parts.join(' ') : '';
      },
      country: 'country',
      state: 'administrative_area_level_1',
      city: 'locality',
      zip: 'postal_code',
      unitNumber: 'subpremise',
    };

    Object.entries(mapping).forEach(([key, formControlName]) => {
      if (formControlName && form.get(formControlName)) {
        const value = typeof addressMapping[key] === 'function' ? addressMapping[key](address) : address[addressMapping[key]];
        form.get(formControlName).setValue(value);
      }
    });
  }

  getCountryCode(countryName: string, countryList: any[]): string | null {
    const country = countryList.find((c) => c.name.toLowerCase() === countryName.toLowerCase());
    return country ? country.isoCode : null;
  }

  getAddrComponent(place: any, componentTemplate: any): string | undefined {
    let result;
    for (let i = 0; i < place.address_components.length; i++) {
      const addressType = place.address_components[i].types[0];
      if (componentTemplate[addressType]) {
        result = place.address_components[i][componentTemplate[addressType]];
        return result;
      }
    }
    return;
  }

  getStreet(place: any): string {
    const COMPONENT_TEMPLATE = { route: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE) || '';
  }

  getCountry(place: any): string {
    const COMPONENT_TEMPLATE = { country: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE) || '';
  }

  getCountryShort(place: any): string {
    const COMPONENT_TEMPLATE = { country: 'short_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE) || '';
  }

  getState(place: any): string {
    const COMPONENT_TEMPLATE = { administrative_area_level_1: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE) || '';
  }

  getStateShort(place: any): string {
    const COMPONENT_TEMPLATE = { administrative_area_level_1: 'short_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE) || '';
  }

  getCity(place: any): string {
    const COMPONENT_TEMPLATE = { locality: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE) || '';
  }

  getPostCode(place: any): string {
    const COMPONENT_TEMPLATE = { postal_code: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE) || '';
  }

  getPhone(place: any): string {
    const COMPONENT_TEMPLATE = { formatted_phone_number: 'formatted_phone_number' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE) || '';
  }

  getDistrict(place: any): string {
    const COMPONENT_TEMPLATE = { administrative_area_level_2: 'short_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE) || '';
  }
}
